'use client'

import { Formik, FormikHelpers } from 'formik'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import * as Yup from 'yup'

import { Spacer } from '@syconium/little-miss-figgy'

import { FormStatus } from '../../../../app/_components/form/FormStatus'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { ButtonWrapper } from '../../account/styles'

import { Form } from './form/Form'
import { FormInput } from './form/FormInput'
import { FormSubmit } from './form/FormSubmit'
import { OrderLookupFormText } from './styles'

export const EMAIL_FORM_NAME = 'email'
export const ORDER_NUMBER_FORM_NAME = 'orderNumber'

export type LookupCreateFormValues = {
  email: string
  orderNumber: string
}

type FormProviderProps = {
  children?: React.ReactNode
}

export const FormProvider = ({ children }: FormProviderProps) => {
  const router = useRouter()
  const {
    magnolia: {
      orders: ordersStrings,
      general: { emailWarning },
    },
  } = useTranslation()

  const initialValues = useMemo<LookupCreateFormValues>(
    () => ({
      email: '',
      orderNumber: '',
    }),
    []
  )

  const onSubmit = (
    values: LookupCreateFormValues,
    actions: FormikHelpers<LookupCreateFormValues>
  ) => {
    actions.setStatus(undefined)

    let orderNumber = values.orderNumber
    let email = values.email
    orderNumber = encodeURIComponent(orderNumber)
    email = encodeURIComponent(email)
    router.push(`lookup/order/?number=${orderNumber}&email=${email}`)
  }

  const validationSchema = () => {
    return Yup.object().shape({
      email: Yup.string().email().required(emailWarning),
      orderNumber: Yup.string()
        .min(3, ordersStrings.orderNotValid)
        .max(10, ordersStrings.orderNotValid)
        .matches(/^[0-9]+$/, ordersStrings.orderDigitsOnly)
        .required(ordersStrings.orderWarning),
    })
  }

  return (
    <Formik onSubmit={onSubmit} validationSchema={validationSchema} initialValues={initialValues}>
      {children}
    </Formik>
  )
}

export const LookupCreateForm = () => {
  const {
    magnolia: { general: generalStrings, orders: ordersStrings },
  } = useTranslation()
  return (
    <FormProvider>
      <Form>
        <OrderLookupFormText id='orderNumberLabel' asTag='span' required>
          {ordersStrings.orderNumber}
        </OrderLookupFormText>
        <Spacer height={8} />
        <FormInput
          name={ORDER_NUMBER_FORM_NAME}
          type='text'
          placeholder={ordersStrings.orderNumberPlaceHolder}
        />

        <Spacer height={24} />

        <OrderLookupFormText id='emailLabel' asTag='span' required>
          {generalStrings.email}
        </OrderLookupFormText>
        <Spacer height={8} />
        <FormInput
          name={EMAIL_FORM_NAME}
          type='email'
          placeholder={ordersStrings.emailPlaceHolder}
        />

        <ButtonWrapper>
          <FormStatus />
          <FormSubmit submittingLabel={generalStrings.submit}>{generalStrings.submit}</FormSubmit>
        </ButtonWrapper>
      </Form>
    </FormProvider>
  )
}
